import type { ClientHomePlanPublic } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { assertIsNotNull, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import type { BuilderModalStep } from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import BuilderModalStepped from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import { homePlanPath } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { moveHomePlan } from "lib/storage/homePlanStorage";
import { useState } from "react";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import { ChooseLocation } from "./ChooseLocation";
import { ChooseLocationPlan } from "./ChooseLocationPlan";
import styles from "./MoveHomePlanModal.module.scss";
import type { OptionChoiceState } from "./MoveHomePlanTypes";
import { ReviewOptionChanges } from "./ReviewOptionChanges";

interface MoveHomePlanModalProps {
  homePlan: ClientHomePlanPublic;
  onClose: () => void;
}

export default function MoveHomePlanModal({ homePlan, onClose }: MoveHomePlanModalProps) {
  const { setUserNotification } = useAppContext();
  const { builder } = useUserContext();
  const [selectedLocation, setSelectedLocation] = useState<ClientLocationWithLevel | null>(null);
  const [selectedLocationPlan, setSelectedLocationPlan] = useState<ClientLocationPlan | null>(null);
  const [optionChoiceState, setOptionChoiceState] = useState<OptionChoiceState>({
    optionChanges: {},
    sourceOptionSets: {},
    targetOptionSets: {},
  });

  const navigate = useAppNavigate();

  assertIsNotUndefined(builder);

  const handleOnSubmit = async (setSaving?: (saving: boolean) => void) => {
    assertIsNotNull(homePlan.id);
    if (!selectedLocationPlan) {
      console.error("Selected location plan is null");
      setUserNotification({ messageType: "error", message: "Please select a location plan." });
      return;
    }

    const optionChoices: Record<string, string> = {};
    for (const [optionSetId, optionChange] of Object.entries(optionChoiceState.optionChanges)) {
      optionChoices[optionSetId] = optionChange.choiceOverride ?? optionChange.newOptionChoice;
    }

    const [, err] = await moveHomePlan({
      builderId: builder.id,
      homePlanId: homePlan.id,
      moveParams: {
        locationPlanId: selectedLocationPlan.id,
        optionChanges: optionChoiceState.optionChanges,
      },
    });

    if (err) {
      setUserNotification(err);
    } else {
      setUserNotification({ messageType: "success", message: "Moved Home Plan." });
      await navigate({ to: homePlanPath(builder.id, homePlan.id) });
    }

    setSaving?.(false);
  };

  const modalSteps: BuilderModalStep[] = [
    {
      header: "Select a Location",
      subheader: "Choose a location to move this Home Plan to.",
      body: () => (
        <div className={styles.stepBody}>
          <ChooseLocation
            builder={builder}
            selectedLocation={selectedLocation}
            onSelect={(location: ClientLocationWithLevel | null) => setSelectedLocation(location)}
          />
        </div>
      ),
      submittable: selectedLocation !== null,
      notSubmittableTooltipText: "Please select a location",
    },
    {
      header: "Confirm Location Plan",
      subheader: "Confirm the Location Plan that this Home Plan will inherit options from",
      body: () => (
        <div className={styles.stepBody}>
          <ChooseLocationPlan
            homePlan={homePlan}
            builder={builder}
            selectedLocationPlan={selectedLocationPlan}
            location={ensureDefined(selectedLocation)} // may want to reconsider this, but it shouldn't be possible to get to this step without this value set
            onSelect={(plan: ClientLocationPlan) => setSelectedLocationPlan(plan)}
          />
        </div>
      ),
      submittable: selectedLocationPlan !== null,
    },
    {
      header: "Review option changes",
      body: () => (
        <div className={styles.stepBody}>
          {!selectedLocationPlan || !selectedLocation ? (
            <p>Something went wrong. Please try again.</p>
          ) : (
            <ReviewOptionChanges
              builder={builder}
              targetLocationPlanId={selectedLocationPlan?.id}
              targetLocationId={selectedLocation?.id}
              homePlanId={homePlan.id}
              optionChoiceState={optionChoiceState}
              setOptionChoiceState={setOptionChoiceState}
            />
          )}
        </div>
      ),
      submittable: selectedLocation !== null,
    },
  ];

  return (
    <BuilderModalStepped
      steps={modalSteps}
      modalProps={{ open: true, onClose, size: "medium" }}
      onSubmit={handleOnSubmit}
    />
  );
}
